import { ObjectTypes } from '@diligentcorp/rc-service-object-mapping';
import { useApi } from '@issuemanager/api-client';
import {
  ExtendedAxiosError,
  useAppConfig,
} from '@issuemanager/apps/client/hooks';
import { useFeatureFlags } from '@issuemanager/client/features/users';
import { UseQueryResult, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface RelationshipTypeData {
  value: string;
  label: string;
}

export function useRelationshipTypes(): {
  types: RelationshipTypeData[];
  isLoading: boolean;
  error: ExtendedAxiosError | null;
} {
  const { t } = useTranslation();
  const api = useApi();
  const appConfig = useAppConfig();
  const isIssueRelationshipsEnabled = useFeatureFlags(
    'issueRelationships',
  ) as boolean;
  const isExtendedGrcTypesEnabled = useFeatureFlags(
    'relationshipExtendedGrcTypes',
  ) as boolean;

  const {
    data: relationshipTypes = [],
    isLoading,
    error,
  }: UseQueryResult<readonly string[], ExtendedAxiosError> = useQuery({
    queryKey: ['relationship-types', isExtendedGrcTypesEnabled],
    queryFn: async () => {
      if (appConfig.isLocalDev) {
        const allTypes = [
          ObjectTypes.EVIDENCE,
          ObjectTypes.CONTROL,
          ObjectTypes.PROCESS,
          ObjectTypes.OBJECTIVE,
          ObjectTypes.CONTROL_ASSESSMENT,
          ObjectTypes.RISK,
          ObjectTypes.RISK_MITIGATION,
          ObjectTypes.RISK_EVENT_ASSESSMENT,
        ];
        return isExtendedGrcTypesEnabled ? allTypes : [ObjectTypes.EVIDENCE];
      }

      const response = await api.relationshipsApi.getRelationshipTypes();
      return response.data;
    },
    enabled: isIssueRelationshipsEnabled,
  });

  const types: RelationshipTypeData[] = useMemo(() => {
    const allowedTypes = isExtendedGrcTypesEnabled
      ? relationshipTypes
      : relationshipTypes.filter((type) => type === ObjectTypes.EVIDENCE);
    return allowedTypes.map((type) => ({
      value: type,
      label: t(`relationshipTypes.${type}`, type),
    }));
  }, [relationshipTypes, isExtendedGrcTypesEnabled, t]);

  return {
    types,
    isLoading,
    error,
  };
}
